import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { api, formattaOra } from "@/lib/api";

const CHIAVE_ID = "sos_chat_id";
const CHIAVE_NOME = "sos_chat_nome";

export function ChatPanel({ prefisso = "chat-widget" }) {
  const [convId, setConvId] = useState(() => localStorage.getItem(CHIAVE_ID) || "");
  const [nome, setNome] = useState(() => localStorage.getItem(CHIAVE_NOME) || "");
  const [telefono, setTelefono] = useState("");
  const [messaggi, setMessaggi] = useState([]);
  const [stato, setStato] = useState("Aperta");
  const [testo, setTesto] = useState("");
  const [caricamento, setCaricamento] = useState(false);
  const fondoRef = useRef(null);

  const carica = useCallback(async () => {
    if (!convId) return;
    try {
      const { data } = await api.get(`/chat/conversazioni/${convId}/messaggi`);
      setMessaggi(data.messaggi);
      setStato(data.stato);
    } catch {
      localStorage.removeItem(CHIAVE_ID);
      setConvId("");
    }
  }, [convId]);

  useEffect(() => {
    if (!convId) return;
    carica();
    const t = setInterval(carica, 4000);
    return () => clearInterval(t);
  }, [convId, carica]);

  useEffect(() => {
    fondoRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messaggi]);

  const inizia = async (e) => {
    e.preventDefault();
    setCaricamento(true);
    try {
      const { data } = await api.post("/chat/conversazioni", { nome, telefono });
      localStorage.setItem(CHIAVE_ID, data.id);
      localStorage.setItem(CHIAVE_NOME, nome);
      setConvId(data.id);
    } catch {
      /* riprova al prossimo invio */
    } finally {
      setCaricamento(false);
    }
  };

  const invia = async (e) => {
    e.preventDefault();
    if (!testo.trim()) return;
    const msg = testo;
    setTesto("");
    await api.post(`/chat/conversazioni/${convId}/messaggi`, { testo: msg });
    carica();
  };

  const inputCls =
    "w-full rounded-xl border border-slate-700 bg-obsidian/70 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none focus:border-amber-500";

  return (
    <div className="flex h-full flex-col" data-testid={`${prefisso}-panel`}>
      <div className="flex items-center justify-center border-b border-slate-800 bg-abyss px-5 py-4 text-center">
        <div>
          <p className="font-display text-sm font-bold text-white">Chatta con noi</p>
          <p className="mt-0.5 flex items-center justify-center gap-1.5 text-[11px] text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Online · rispondiamo in pochi minuti
          </p>
        </div>
      </div>

      {!convId ? (
        <form onSubmit={inizia} className="flex flex-1 flex-col justify-center gap-4 p-6">
          <p className="text-center text-sm leading-relaxed text-slate-300">
            Ciao! Presentati e descrivici la tua emergenza: un operatore ti risponde
            subito. Puoi anche lasciare un messaggio e farti richiamare.
          </p>
          <input
            data-testid={`${prefisso}-nome-input`}
            required
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Il tuo nome *"
            className={inputCls}
          />
          <input
            data-testid={`${prefisso}-telefono-input`}
            type="tel"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            placeholder="Telefono (facoltativo)"
            className={inputCls}
          />
          <button
            type="submit"
            data-testid={`${prefisso}-start-btn`}
            disabled={caricamento}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 py-3.5 text-sm font-bold text-obsidian transition-colors hover:bg-amber-400 disabled:opacity-60"
          >
            {caricamento && <Loader2 size={16} className="animate-spin" />}
            Inizia la conversazione
          </button>
        </form>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid={`${prefisso}-messaggi`}>
            <div className="mx-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-800 px-4 py-3 text-left text-sm text-slate-200">
              Ciao {nome}! Siamo il team di SOS Riparazioni 24. Descrivici il problema:
              un operatore ti risponde in pochi minuti.
            </div>
            {messaggi.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.mittente === "visitatore" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-left text-sm ${
                    m.mittente === "visitatore"
                      ? "rounded-tr-sm bg-amber-500 text-obsidian"
                      : "rounded-tl-sm bg-slate-800 text-slate-200"
                  }`}
                >
                  <p>{m.testo}</p>
                  <p
                    className={`mt-1 text-[10px] ${
                      m.mittente === "visitatore" ? "text-obsidian/60" : "text-slate-500"
                    }`}
                  >
                    {formattaOra(m.created_at)}
                  </p>
                </div>
              </div>
            ))}
            {stato === "Chiusa" && (
              <p className="pt-2 text-center text-xs text-slate-500">
                Conversazione chiusa dall'operatore. Scrivici pure per riaprirla.
              </p>
            )}
            <div ref={fondoRef} />
          </div>
          <form onSubmit={invia} className="flex items-center gap-2 border-t border-slate-800 p-3.5">
            <input
              data-testid={`${prefisso}-input-field`}
              value={testo}
              onChange={(e) => setTesto(e.target.value)}
              placeholder="Scrivi un messaggio…"
              className={inputCls}
            />
            <button
              type="submit"
              data-testid={`${prefisso}-send-btn`}
              aria-label="Invia messaggio"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-500 text-obsidian transition-colors hover:bg-amber-400"
            >
              <Send size={17} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
